import type {
  ContratCxpiDossierDTO,
  ContratCxpiDTO,
  LovCxpiDTO,
  ModuleDTO,
} from '@/features/api/extranet/dto/Demande/CXPI'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import type {
  RechercheQueryDossierReservationFondsDTO,
  RechercheResultDossierReservationFondsDTO,
} from '@/features/api/extranet/dto/Dossier/Recherche/ReservationFonds'
import type { AdresseDTO } from '@/features/api/extranet/dto/shared'
import type { IResponse } from '@/features/api/extranet/dto/types'
import { demandeToLocation } from '@/features/application/locations'
import type { BaseCalculFrais, ModeFrais } from '@/features/domaine/types/opco'
import {
  ConstantesCIPI,
  NatureFraisAvecQuantite,
  NaturesFrais,
  TypeModule,
} from '@/features/domaine/types/opco/Demande/CXPI'
import { ensureSuccessStatusCode } from '@/features/fetch/ensureSuccessStatusCode'
import { useFetch } from '@/features/fetch/useFetch'
import type { PagedRequest } from '@/features/interface/recherches/shared/composables/storeRechercheBuilder.ts'
import {
  EndPointsDrfDemandeCxpi,
  EndPointsDrfDossierCxpi,
  EndPointsFormationDemandeCxpi,
  locationToEndPointsDemande,
} from '@/features/services'
import { buildDemandeService } from '@/features/services/demande/buildDemandeService.ts'
import { loadRecherche } from '../../recherche'
import type { NavigateToDemandeSor } from '@/router/navigateTo'

export const cxpiFormationDemandeServices = buildDemandeService<'MODEL_CXPI', LovCxpiDTO>({
  location: EndPointsFormationDemandeCxpi.location,
  loadExtraData,
  normaliserData,
})

export const cxpiDrfDemandeServices = buildDemandeService<'MODEL_CXPI', LovCxpiDTO>({
  location: EndPointsDrfDemandeCxpi.location,
  normaliserData,
})

export const cxpiDrfDossierServices = buildDemandeService<'MODEL_CXPI', LovCxpiDTO>({
  location: EndPointsDrfDossierCxpi.location,
  normaliserData: normaliserDataDossier,
})

function normaliserData(data: ContratCxpiDTO) {
  //data.typeDemande ??= TypeDemandeDossier
  data.contrat ??= {}
  data.employeur ??= {}
  data.fichiers ??= []
  data.formation ??= {}

  if (data.typeDemande === 'Formation') {
    data.contrat!.interruption ??= []
    // Si effectif à 0 => forcer à null
    if (data.employeur?.effectifTotalEntreprise === 0) data.employeur!.effectifTotalEntreprise = null
  }

  data.formation!.modules ??= []
  if (data.typeDemande === 'Formation') {
    data.formation!.modules = data.formation!.modules?.map((module) => ({
      ...module,
      fraisPedagogiques: updateFrais(module.fraisPedagogiques!, initialiseListeFrais('Pedagogique'), false),
      fraisAnnexesSalarie: updateFrais(module.fraisAnnexesSalarie!, initialiseListeFrais('Annexe'), false),
    }))

    data.formation!.modules = sortModules(data.formation!.modules)
  }
  if (data.sousDispositif === 'CIPI') data.formation!.dureeMinimumMission ??= ConstantesCIPI.dureeMinMissionEnHeure

  data.financement ??= {}

  data.mission ??= {}
  if (data.typeDemande === 'Formation') {
    data.salarie ??= {}
    data.salarie!.adresse ??= {} as AdresseDTO
  }

  return data
}

function normaliserDataDossier(data: ContratCxpiDTO) {
  //data.typeDemande ??= TypeDemandeDossier
  data.contrat ??= {}
  data.employeur ??= {}
  data.fichiers ??= []
  data.formation ??= {}

  data.formation!.modules ??= []

  data.financement ??= {}

  data.mission ??= {}

  return data
}

async function loadExtraData(data: ContratCxpiDTO, init?: RequestInit) {
  if (data.typeDemande !== 'Formation') return data
  if (!data.contrat?.codeDrf) throw Error('Le code DRF est manquant, impossible de charger le dossier associé.')

  const codeDrf = data.contrat.codeDrf

  const location = demandeToLocation({
    typeDemande: 'ReservationFonds',
    origine: 'sor',
    dispositif: data.dispositif,
    sousDispositif: data.sousDispositif,
    code: codeDrf,
  }) as NavigateToDemandeSor

  // Charge le dossier DRF
  const drfPromise = loadExtraDataDossierDRF(location, init)
  const drfComplémentPromise = loadExtraDataRechercheDossierDRF(codeDrf, init)

  return await Promise.all([drfPromise, drfComplémentPromise]).then(([drf, drfComplément]) => {
    if (drfComplément) {
      drf.data.demandes = drfComplément.demandes
      drf.data.dossiersLies = drfComplément.dossiersLies
      drf.data.statut = drfComplément.statut
    }

    return {
      ...data,
      drf: drf.data,
    }
  })
}

export async function loadExtraDataDossierDRF(location: NavigateToDemandeSor, init?: RequestInit) {
  const path = locationToEndPointsDemande(location).endPointEntite
  const code = location.code

  // Charge le dossier DRF
  const fetch = useFetch('extranet')
  const response = await fetch(`${path}/${code}`, init)
  ensureSuccessStatusCode(response)
  return (await response.json()) as IResponse<ContratCxpiDossierDTO>
}

async function loadExtraDataRechercheDossierDRF(code: string, init?: RequestInit) {
  const querySearch = [code]
  // Charge la réservation de fonds pour récupérer les dossiers et demande liées => à voir si on modifie pour l'intégrer dans l'appel du détail
  const request: PagedRequest<RechercheQueryDossierReservationFondsDTO> = {
    query: {
      search: querySearch,
      statuts: [],
    },
    orderBy: [],
    pagination: { pageNumber: 1, pageSize: 1 },
  } as const

  const resultDrf = await loadRecherche<
    RechercheQueryDossierReservationFondsDTO,
    RechercheResultDossierReservationFondsDTO
  >(EndPointsDrfDossierCxpi.endPoints.endPointEntite, request, init)

  return resultDrf.data[0] ?? null
}

function updateFrais(existants: FraisDTO[], reference: FraisDTO[], remplacer = false): FraisDTO[] {
  const naturesExistantes = new Set(existants.map((f) => f.nature))

  const manquants = reference.filter((ref) => !naturesExistantes.has(ref.nature))

  // si remplacer = true → on écrase les existants par la ref
  const fusion = remplacer ? reference : [...existants, ...manquants]

  return fusion.map((f) => ({
    ...f,
    montant: f.montant === 0 ? null : f.montant,
  }))
}

function initialiseListeFrais(mode: ModeFrais) {
  return NaturesFrais[mode].map((nature) => {
    const baseCalcul: BaseCalculFrais = NatureFraisAvecQuantite.includes(nature) ? 'Quantite' : 'Montant'
    return {
      mode,
      nature,
      baseCalcul,
      montant: null,
      quantite: null,
    } as FraisDTO
  })
}

// Trie les modules par typeModule et dateDebut
function sortModules(modules: ModuleDTO[]): ModuleDTO[] {
  return [...modules]
    .sort((a, b) => {
      // Tri par date de debut
      const dateA = a.dateDebut ?? ''
      const dateB = b.dateDebut ?? ''
      return dateA > dateB ? 1 : dateA < dateB ? -1 : 0
    })
    .sort((a, b) => {
      // Tri par typeModule
      const indexA = a.typeModule ? TypeModule.indexOf(a.typeModule) : Number.MAX_SAFE_INTEGER
      const indexB = b.typeModule ? TypeModule.indexOf(b.typeModule) : Number.MAX_SAFE_INTEGER
      return indexA > indexB ? 1 : indexA < indexB ? -1 : 0
    })
}
